// Right-side overlay panel. Backdrop click + Esc both invoke onClose.
//
// 2026-07-04: render via portal to document.body. Some callers mount the
// Drawer inside a `<td className="hidden">` (valid-HTML wrapper for a modal
// that lives in a <tr>, e.g. CapitalCallsPanel's per-row Money In / Adjust).
// `display:none` on that ancestor collapsed the fixed panel to 0×0 — the
// button "did nothing". Portaling out of the table subtree fixes every
// caller (a fixed overlay doesn't care where in the DOM it mounts).
//
// 2026-07-16 (UX 稽核 #1): `dirty` 守門 — 表單 drawer 填到一半誤點背景 / 按 Esc
// 會整張丟失。呼叫端傳 dirty=true 時, 背景點擊與 Esc 先 confirm 才關;
// 右上 × 也一樣。標題列的關閉鈕與程式化 onClose (儲存成功後) 不受影響 —
// 儲存路徑呼叫的是自己的 onSaved/onClose, 不經過這層。

import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useConfirmDiscard } from '../lib/confirm.tsx';

interface DrawerProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Stack on top of another open drawer (raises z-index + tightens panel) */
  nested?: boolean;
  /** Wider panel for spreadsheet-style forms (e.g. 報帳明細多欄一列)。 */
  wide?: boolean;
  /** 表單有未儲存變更 — 背景點擊 / Esc / × 關閉前先確認, 防誤觸丟失。 */
  dirty?: boolean;
}

export function Drawer({
  title,
  onClose,
  children,
  nested = false,
  wide = false,
  dirty = false,
}: DrawerProps) {
  // 2026-08-06: 原生 confirm() → 頁內對話框（見 lib/confirm.tsx）。
  // 這裡非同步了，但語意不變：使用者按「關閉」才真的關。
  const confirmDiscard = useConfirmDiscard();
  const guardedClose = () => {
    if (!dirty) {
      onClose();
      return;
    }
    void confirmDiscard().then((ok) => {
      if (ok) onClose();
    });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') guardedClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  const backdropZ = nested ? 'z-[60]' : 'z-40';
  const panelZ = nested ? 'z-[70]' : 'z-50';
  const panelWidth = nested ? 'max-w-xl' : wide ? 'max-w-4xl' : 'max-w-2xl';

  return createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/30 ${backdropZ}`}
        onClick={guardedClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={title}
        className={`fixed right-0 top-0 bottom-0 w-full ${panelWidth} bg-white shadow-2xl ${panelZ} flex flex-col`}
      >
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={guardedClose}
            className="text-slate-500 hover:text-slate-900 text-2xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-6">{children}</div>
      </aside>
    </>,
    document.body,
  );
}
